import { describe, it } from 'mocha'
import { expect } from 'chai'

import findPhoneNumbersInText from './findPhoneNumbersInText.js'
import metadata from '../metadata.min.json' with { type: 'json' }
import metadataMax from '../metadata.max.json' with { type: 'json' }

function findPhoneNumbersInTextWithResults(input, options, metadata) {
	const results = findPhoneNumbersInText(input, options, metadata)
	return results.map((result) => {
		const { startsAt, endsAt, number } = result
		const data = {
			phone: number.nationalNumber,
			startsAt,
			endsAt
		}
		if (number.country) {
			data.country = number.country
		}
		if (number.ext) {
			data.ext = number.ext
		}
		return data
	})
}

describe('findPhoneNumbersInText', () => {
	it('should find phone numbers in text (with default country)', () => {
		expect(
            findPhoneNumbersInText('+7 (800) 555-35-35', 'US', metadata)[0].number.number
        ).to.equal('+78005553535')
	})

	it('should find phone numbers in text (with default country in options)', () => {
		expect(
            findPhoneNumbersInText('+7 (800) 555-35-35', { defaultCountry: 'US' }, metadata)[0].number.number
        ).to.equal('+78005553535')
	})

	it('should find phone numbers in text (with default country and options)', () => {
		expect(
            findPhoneNumbersInText('+7 (800) 555-35-35', 'US', {}, metadata)[0].number.number
        ).to.equal('+78005553535')
	})

	it('should find phone numbers in text (without default country, with options)', () => {
		expect(
            findPhoneNumbersInText('+7 (800) 555-35-35', undefined, {}, metadata)[0].number.number
        ).to.equal('+78005553535')
	})

	it('should find phone numbers in text (with default country, without options)', () => {
		expect(
            findPhoneNumbersInText('+7 (800) 555-35-35', 'US', undefined, metadata)[0].number.number
        ).to.equal('+78005553535')
	})

	it('should find phone numbers in text (with empty default country)', () => {
		expect(
            findPhoneNumbersInText('+7 (800) 555-35-35', undefined, metadata)[0].number.number
        ).to.equal('+78005553535')
	})

	it('should find phone numbers in text', () => {
		const NUMBERS = ['+78005553535', '+12133734253']
		const results = findPhoneNumbersInText('The number is +7 (800) 555-35-35 and not (213) 373-4253 as written in the document.', metadata)
		let i = 0
		while (i < results.length) {
			expect(results[i].number.number).to.equal(NUMBERS[i])
			i++
		}
	})

	it('should find phone numbers in text (default country calling code)', () => {
		const NUMBERS = ['+870773111632']
		const results = findPhoneNumbersInText('The number is 773 111 632', { defaultCallingCode: '870' }, metadata)
		let i = 0
		while (i < results.length) {
			expect(results[i].number.number).to.equal(NUMBERS[i])
			i++
		}
	})

	it('should find numbers', () => {
		expect(
            findPhoneNumbersInTextWithResults('2133734253', { defaultCountry: 'US' }, metadata)
        ).to.deep.equal([{
			phone    : '2133734253',
			country  : 'US',
			startsAt : 0,
			endsAt   : 10
		}])

		expect(
            findPhoneNumbersInTextWithResults('(213) 373-4253', { defaultCountry: 'US' }, metadata)
        ).to.deep.equal([{
			phone    : '2133734253',
			country  : 'US',
			startsAt : 0,
			endsAt   : 14
		}])

		expect(
            findPhoneNumbersInTextWithResults('The number is +7 (800) 555-35-35 and not (213) 373-4253 as written in the document.', { defaultCountry: 'US' }, metadata)
        ).to.deep.equal([{
			phone    : '8005553535',
			country  : 'RU',
			startsAt : 14,
			endsAt   : 32
		}, {
			phone    : '2133734253',
			country  : 'US',
			startsAt : 41,
			endsAt   : 55
		}])

		// Opening parenthesis issue.
		// https://github.com/catamphetamine/libphonenumber-js/issues/252
		expect(
            findPhoneNumbersInTextWithResults('The number is +7 (800) 555-35-35 and not (213) 373-4253 (that\'s not even in the same country!) as written in the document.', { defaultCountry: 'US' }, metadata)
        ).to.deep.equal([{
			phone    : '8005553535',
			country  : 'RU',
			startsAt : 14,
			endsAt   : 32
		}, {
			phone    : '2133734253',
			country  : 'US',
			startsAt : 41,
			endsAt   : 55
		}])

		// No default country.
		expect(
            findPhoneNumbersInTextWithResults('The number is +7 (800) 555-35-35 as written in the document.', undefined, metadata)
        ).to.deep.equal([{
			phone    : '8005553535',
			country  : 'RU',
			startsAt : 14,
			endsAt   : 32
		}])

		// Passing `options` and default country.
		expect(
            findPhoneNumbersInTextWithResults('The number is +7 (800) 555-35-35 as written in the document.', { defaultCountry: 'US', leniency: 'VALID' }, metadata)
        ).to.deep.equal([{
			phone    : '8005553535',
			country  : 'RU',
			startsAt : 14,
			endsAt   : 32
		}])

		// Passing `options`.
		expect(
            findPhoneNumbersInTextWithResults('The number is +7 (800) 555-35-35 as written in the document.', { leniency: 'VALID' }, metadata)
        ).to.deep.equal([{
			phone    : '8005553535',
			country  : 'RU',
			startsAt : 14,
			endsAt   : 32
		}])

		// Not a phone number and a phone number.
		expect(
            findPhoneNumbersInTextWithResults('Digits 12 are not a number, but +7 (800) 555-35-35 is.', { leniency: 'VALID' }, metadata)
        ).to.deep.equal([{
			phone    : '8005553535',
			country  : 'RU',
			startsAt : 32,
			endsAt   : 50
		}])

		// Phone number extension.
		expect(
            findPhoneNumbersInTextWithResults('Date 02/17/2018 is not a number, but +7 (800) 555-35-35 ext. 123 is.', { leniency: 'VALID' }, metadata)
        ).to.deep.equal([{
			phone    : '8005553535',
			country  : 'RU',
			ext      : '123',
			startsAt : 37,
			endsAt   : 64
		}])
	})

	it('should find numbers (v2)', () => {
		const phoneNumbers = findPhoneNumbersInText('The number is +7 (800) 555-35-35 ext. 1234 and not (213) 373-4253 as written in the document.', { defaultCountry: 'US', v2: true }, metadata)

		expect(phoneNumbers.length).to.equal(2)

		expect(phoneNumbers[0].startsAt).to.equal(14)
		expect(phoneNumbers[0].endsAt).to.equal(42)

		expect(phoneNumbers[0].number.number).to.equal('+78005553535')
		expect(phoneNumbers[0].number.nationalNumber).to.equal('8005553535')
		expect(phoneNumbers[0].number.country).to.equal('RU')
		expect(phoneNumbers[0].number.countryCallingCode).to.equal('7')
		expect(phoneNumbers[0].number.ext).to.equal('1234')

		expect(phoneNumbers[1].startsAt).to.equal(51)
		expect(phoneNumbers[1].endsAt).to.equal(65)

		expect(phoneNumbers[1].number.number).to.equal('+12133734253')
		expect(phoneNumbers[1].number.nationalNumber).to.equal('2133734253')
		expect(phoneNumbers[1].number.country).to.equal('US')
		expect(phoneNumbers[1].number.countryCallingCode).to.equal('1')
	})

	it('shouldn\'t find non-valid numbers', () => {
		// Not a valid phone number for US.
		expect(
            findPhoneNumbersInTextWithResults('1111111111', { defaultCountry: 'US' }, metadata)
        ).to.deep.equal([])
	})

	it('should find non-European digits', () => {
		// E.g. in Iraq they don't write `+442323234` but rather `+٤٤٢٣٢٣٢٣٤`.
		expect(
            findPhoneNumbersInTextWithResults('العَرَبِيَّة‎ +٤٤٣٣٣٣٣٣٣٣٣٣عَرَبِيّ‎', undefined, metadata)
        ).to.deep.equal([{
			country  : 'GB',
			phone    : '3333333333',
			startsAt : 14,
			endsAt   : 27
		}])
	})

	it('should work in edge cases', () => {
		let thrower

		// No input
		expect(findPhoneNumbersInTextWithResults('', undefined, metadata)).to.deep.equal([])

		// // No country metadata for this `require` country code
		// thrower = () => findPhoneNumbersInTextWithResults('123', { defaultCountry: 'ZZ' }, metadata)
		// thrower.should.throw('Unknown country')

		// Numerical `value`
		thrower = () => findPhoneNumbersInTextWithResults(2141111111, { defaultCountry: 'US' })
		expect(thrower).to.throw('A text for parsing must be a string.')

		// // No metadata
		// thrower = () => findPhoneNumbersInTextWithResults('')
		// thrower.should.throw('`metadata` argument not passed')

		// No metadata, no default country, no phone numbers.
		expect(findPhoneNumbersInTextWithResults('')).to.deep.equal([])
	})

	it('should find international numbers when passed a non-existent default country', () => {
		const numbers = findPhoneNumbersInText('Phone: +7 (800) 555 35 35. National: 8 (800) 555-55-55', { defaultCountry: 'XX', v2: true }, metadata)
		expect(numbers.length).to.equal(1)
		expect(numbers[0].number.nationalNumber).to.equal('8005553535')
	})

	it('shouldn\'t find phone numbers which are not phone numbers', () => {
		// A timestamp.
		expect(
            findPhoneNumbersInTextWithResults('2012-01-02 08:00', { defaultCountry: 'US' }, metadata)
        ).to.deep.equal([])

		// A valid number (not a complete timestamp).
		expect(
            findPhoneNumbersInTextWithResults('2012-01-02 08', { defaultCountry: 'US' }, metadata)
        ).to.deep.equal([{
			country  : 'US',
			phone    : '2012010208',
			startsAt : 0,
			endsAt   : 13
		}])

		// Invalid parens.
		expect(
            findPhoneNumbersInTextWithResults('213(3734253', { defaultCountry: 'US' }, metadata)
        ).to.deep.equal([])

		// Letters after phone number.
		expect(
            findPhoneNumbersInTextWithResults('2133734253a', { defaultCountry: 'US' }, metadata)
        ).to.deep.equal([])

		// Valid phone (same as the one found in the UUID below).
		expect(
            findPhoneNumbersInTextWithResults('The phone number is 231354125.', { defaultCountry: 'FR' }, metadata)
        ).to.deep.equal([{
			country  : 'FR',
			phone    : '231354125',
			startsAt : 20,
			endsAt   : 29
		}])

		// Not a phone number (part of a UUID).
		// Should parse in `{ extended: true }` mode.
		const possibleNumbers = findPhoneNumbersInTextWithResults('The UUID is CA801c26f98cd16e231354125ad046e40b.', { defaultCountry: 'FR', extended: true }, metadata)
		expect(possibleNumbers.length).to.equal(1)
		expect(possibleNumbers[0].country).to.equal('FR')
		expect(possibleNumbers[0].phone).to.equal('231354125')

		// Not a phone number (part of a UUID).
		// Shouldn't parse by default.
		expect(
            findPhoneNumbersInTextWithResults('The UUID is CA801c26f98cd16e231354125ad046e40b.', { defaultCountry: 'FR' }, metadata)
        ).to.deep.equal([])
	})

	// https://gitlab.com/catamphetamine/libphonenumber-js/-/merge_requests/4
	it('should return correct `startsAt` and `endsAt` when matching "inner" candidates in a could-be-a-candidate substring', () => {
		expect(
            findPhoneNumbersInTextWithResults('39945926 77200596 16533084', { defaultCountry: 'ID' }, metadataMax)
        ).to.deep.equal([{
				country: 'ID',
				phone: '77200596',
				startsAt: 9,
				endsAt: 17
			}])
	})
})